import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Dialog,
  DialogContent,
} from '@mui/material';
import { SettingsCard } from '../../components/composed';
import { Button } from '../../components/primitives';
import { ApprovalBanner, AreYouSure } from '../../components/domain/object_management';
import { getCsrfToken } from '../../utils/csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRemoved, setIsRemoved] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);

  useEffect(() => {
    if (id) {
      fetchEvent(id);
    }
  }, [id]);

  const fetchEvent = async (eventId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/events/${eventId}/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Event not found');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setEvent(data);
    } catch (err) {
      console.error('Error fetching event:', err);
      setError(err.message || 'Failed to load event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (event && event.resource) {
      navigate(`/resources/${event.resource}`);
    } else {
      navigate('/facilities');
    }
  };

  const handleApprove = async () => {
    try {
      setUpdating(true);
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/events/${id}/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ is_approved: true, is_active: true }),
      });

      if (!response.ok) {
        throw new Error('Failed to approve event');
      }

      const data = await response.json();
      // Ensure is_approved and is_active are set to true in the state
      setEvent({ ...data, is_approved: true, is_active: true });
    } catch (err) {
      console.error('Error approving event:', err);
      setError('Failed to approve event. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeny = () => {
    setDenyDialogOpen(true);
  };

  const handleDenyCancel = () => {
    setDenyDialogOpen(false);
  };

  const handleDenyConfirm = async () => {
    try {
      setUpdating(true);
      const csrfToken = getCsrfToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (csrfToken) {
        headers['X-CSRFToken'] = csrfToken;
      }

      const response = await fetch(`${API_BASE_URL}/events/${id}/`, {
        method: 'PATCH',
        headers,
        credentials: 'include',
        body: JSON.stringify({ is_active: false }),
      });

      if (!response.ok) {
        throw new Error('Failed to deny event');
      }

      setDenyDialogOpen(false);
      setIsRemoved(true);
    } catch (err) {
      console.error('Error denying event:', err);
      setError('Failed to deny event. Please try again.');
      setDenyDialogOpen(false);
    } finally {
      setUpdating(false);
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    try {
      const date = new Date(dateTimeString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (err) {
      return dateTimeString;
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard title="Event Details" sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard title="Event Details" sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
              <Button variant="outlined" onClick={handleBack}>
                Back to Events
              </Button>
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  if (!event && !isRemoved) {
    return null;
  }

  // Show removed message if event was denied
  if (isRemoved) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: 'background.default',
          padding: 1,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <SettingsCard title="Event Details" sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                This event has been removed.
              </Typography>
              <Button variant="outlined" onClick={handleBack}>
                Back to Events
              </Button>
            </Box>
          </SettingsCard>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: 'background.default',
        padding: 1,
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <SettingsCard title={event.name || "Event Details"} sx={{ mb: 0 }} onEdit={() => {}}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Approval Banner - only shown for unverified events */}
            {!event.is_approved && (
              <ApprovalBanner
                message="This event needs review."
                onApprove={handleApprove}
                onDeny={handleDeny}
                disabled={updating}
              />
            )}

            <Box>
              <Button variant="outlined" onClick={handleBack} sx={{ mb: 3 }}>
                Back to Events
              </Button>
            </Box>

            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Event Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {event.name || 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Start Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTime(event.start_time)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      End Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDateTime(event.end_time)}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Cost
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatCurrency(event.cost)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {event.resource && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Resource
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        <Button
                          variant="text"
                          onClick={() => navigate(`/resources/${event.resource}`)}
                          sx={{
                            textTransform: 'none',
                            padding: 0,
                            minWidth: 'auto',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          View Resource
                        </Button>
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Requires Full Equipment
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {event.requires_full_equipment ? 'Yes' : 'No'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Open to Public
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {event.is_open_to_public ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </SettingsCard>
      </Box>

      {/* Deny Confirmation Dialog */}
      <Dialog open={denyDialogOpen} onClose={handleDenyCancel} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 0 }}>
          <AreYouSure
            message="Are you sure you want to deny this item?"
            onDelete={handleDenyConfirm}
            onCancel={handleDenyCancel}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default EventDetail;

